import React, { useState, useEffect, useMemo, useRef } from 'react';
import $ from 'jquery';
import { ChevronDown, ChevronUp, Plus, Minus, Calendar, Clock, ChevronLeft, X } from 'lucide-react';
import {
  getCountryList,
  getStateListByCountryId,
  getCityListByStateId,
  bindSourceMediumCampaign,
  addLeadWithDependencies,
  uploadLeadProfilePhoto,
  getFollowUpStatus,
  getUserListByUserId,
  getDealPipeline,
  getDealPipelineStage,
  getCollaboratorTypes,
  getTeamsByTypeId,
  getCustomFieldByParentId,
  getProductsByName,
  fetchCountryFlagsAndCodes,
} from '../../utils/lead';
import { getSession } from '../../getSession';
import MediaLibrary from '../MediaLibrary';
import Swal from 'sweetalert2';

if (typeof window !== 'undefined') {
  window.jQuery = window.$ = $;
}


const COUNTRY_CODES = [
  { code: '+91', label: 'IN +91', len: 10 },
  { code: '+1', label: 'US +1', len: 10 },
  { code: '+44', label: 'UK +44', len: 10 },
  { code: '+971', label: 'AE +971', len: 9 },
  { code: '+61', label: 'AU +61', len: 9 },
];

const emptyForm = () => ({
  name: '',
  mobiles: [{ code: '+91', number: '' }],
  emails: [{ address: '' }],
  source: '',
  sourceId: '',
  followupStatus: '',
  followupStatusValue: '',
  medium: '',
  mediumId: '',
  campaign: '',
  campaignId: '',
  assignTo: '',
  salesOwner: '',
  nextStatusDate: new Date().toISOString().split('T')[0],
  nextStatusTime: '00:00',
  conversionDate: new Date().toISOString().split('T')[0],
  conversionTime: '00:00',
  amountPaid: '',
  products: '',
  reAssign: false,
  dealValue: '',
  dealPipeline: '',
  dealStage: '',
  probability: '',
  dealClosureDate: '',
  remarks: '',
  tags: '',
  company: '',
  countryId: '',
  country: '',
  stateId: '',
  state: '',
  cityId: '',
  city: '',
  pinCode: '',
  residentialAddress: '',
  officeAddress: '',
});

const AddLeadForm = ({ onClose, onSaved }) => {
  const [form, setForm] = useState(emptyForm());
  const [showAdvance, setShowAdvance] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [mediumOptions, setMediumOptions] = useState([]);
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [followupStatuses, setFollowupStatuses] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [dealPipelineOptions, setDealPipelineOptions] = useState([]);
  const [dealStageOptions, setDealStageOptions] = useState([]);
  const [selectedFollowup, setSelectedFollowup] = useState(null);

  // ─── collaborator state ───────────────────────────────────────────────────
  const [showCollaborator, setShowCollaborator] = useState(false);
  const [collabTypeOptions, setCollabTypeOptions] = useState([]);
  const [collabTeamOptions, setCollabTeamOptions] = useState([]);
  const [collabMode, setCollabMode] = useState('team');          // 'team' | 'user'
  const [collabSelectedType, setCollabSelectedType] = useState({ id: '', name: '' });
  const [collabSelectedValue, setCollabSelectedValue] = useState({ id: '', name: '' });
  const [collaborators, setCollaborators] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [personImageFile, setPersonImageFile] = useState(null);
  const [personImageName, setPersonImageName] = useState('');
  const [personImagePreview, setPersonImagePreview] = useState('https://kit19.com/assets/custom/img/img_avatar1.png');
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [personImageFromLibrary, setPersonImageFromLibrary] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);

  // Date/Time picker state
  const [showNextDate, setShowNextDate] = useState(false);
  const [showNextTime, setShowNextTime] = useState(false);
  const [showConvDate, setShowConvDate] = useState(false);
  const [showConvTime, setShowConvTime] = useState(false);

  const nextDateRef = useRef(null);
  const nextTimeRef = useRef(null);
  const convDateRef = useRef(null);
  const convTimeRef = useRef(null);
  const productsRef = useRef(null);

  // Products state
  const [productQuery, setProductQuery] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [showProductSuggestions, setShowProductSuggestions] = useState(false);
  const productDebounceRef = useRef(null);

  // Custom Country Flag Picker State
  const [allCountries, setAllCountries] = useState([]);
  const [activeCountryPickerIdx, setActiveCountryPickerIdx] = useState(null);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const countryPickerRef = useRef(null);

  // tag input state
  const [tagsArr, setTagsArr] = useState([]);
  const [tagInput, setTagInput] = useState('');

  // Returns Code as the select value key
  const getFollowupOptionValue = (option) => String(option?.Code ?? option?.code ?? option?.Value ?? option?.value ?? '');
  const customFieldId = (field) => String(field?.FieldId ?? field?.field_ID ?? field?.fieldId ?? field?.id ?? '');
  const customFieldName = (field) => field?.FieldName ?? field?.fieldname ?? field?.Name ?? field?.name ?? '';
  const customFieldType = (field) => String(field?.DataType ?? field?.datatype ?? field?.Type ?? '').toLowerCase();
  const customFieldChoices = (field) => String(field?.Choices ?? field?.choices ?? '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
  const isCustomFieldMandatory = (field) => {
    const raw = field?.IsMandatory ?? field?.isMandatory ?? false;
    if (typeof raw === 'boolean') return raw;
    return String(raw) === '1' || String(raw).toLowerCase() === 'true';
  };

  const updateCustomFieldValue = (field, value) => {
    const id = customFieldId(field);
    setCustomFieldValues(prev => ({ ...prev, [id]: value }));
  };

  const sanitizeFileName = (name = '') => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return '';
    const dotIndex = trimmed.lastIndexOf('.');
    const base = dotIndex > -1 ? trimmed.slice(0, dotIndex) : trimmed;
    const ext = dotIndex > -1 ? trimmed.slice(dotIndex) : '';
    const safeBase = base.replace(/\s+/g, '').replace(/[^a-zA-Z0-9._-]/g, '');
    return `${safeBase}${ext}`;
  };

  const handleAvatarFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setPersonImageFile(null);
      setPersonImageName('');
      setPersonImagePreview('https://kit19.com/assets/custom/img/img_avatar1.png');
      setPersonImageFromLibrary(false);
      setSelectedMedia(null);
      return;
    }

    const sanitized = sanitizeFileName(file.name);
    if (!sanitized) {
      Swal.fire({ icon: 'warning', title: 'Invalid file', text: 'Please choose a valid image file.' });
      return;
    }

    const renamed = new File([file], sanitized, { type: file.type || 'application/octet-stream' });
    setPersonImageFile(renamed);
    setPersonImageName(sanitized);
    setPersonImagePreview(URL.createObjectURL(renamed));
    setPersonImageFromLibrary(false);
    setSelectedMedia(null);
  };

  const validateMandatoryCustomFields = () => {
    for (const field of customFields) {
      if (!isCustomFieldMandatory(field)) continue;
      const id = customFieldId(field);
      const type = customFieldType(field);
      const value = customFieldValues[id];
      const label = customFieldName(field) || 'Custom field';

      if (type === 'checkbox') {
        if (!value) return `${label} is required.`;
        continue;
      }

      if (Array.isArray(value)) {
        if (!value.length) return `${label} is required.`;
        continue;
      }

      if (!String(value ?? '').trim()) return `${label} is required.`;
    }
    return '';
  };

  // ─── fetch reference data on mount ───────────────────────────────────────
  useEffect(() => {
    let alive = true;
    const { TokenId, userId, parentId } = getSession();

    const makeEnvelope = (type, parentid = 0) => ({
      Token: TokenId,
      Message: '',
      LoggedUserId: userId ?? 0,
      MAC_Address: '',
      IP_Address: '',
      Details: { Type: type, Parentid: parentid },
      BroadcastName: '',
    });

    (async () => {
      try {
        const [cl, src, med, cmp, fu, cf, flagsAndCodes] = await Promise.all([
          getCountryList({}),
          bindSourceMediumCampaign(makeEnvelope('SourceName', parentId || 0)),
          bindSourceMediumCampaign(makeEnvelope('MediumName', parentId || 0)),
          bindSourceMediumCampaign(makeEnvelope('CampaignName', parentId || 0)),
          getFollowUpStatus({
            Token: TokenId,
            Message: '',
            LoggedUserId: userId ?? 0,
            MAC_Address: '',
            IP_Address: '',
            Details: { Parentid: parentId || 0 },
            BroadcastName: '',
          }),
          getCustomFieldByParentId({
            Token: TokenId,
            Message: '',
            LoggedUserId: userId ?? 0,
            MAC_Address: '',
            IP_Address: '',
            Details: { ParentId: parentId || 0 },
            BroadcastName: '',
          }),
          fetchCountryFlagsAndCodes(),
        ]);
        if (!alive) return;
        setCountries(Array.isArray(cl) ? cl : []);
        setSourceOptions(Array.isArray(src) ? src : []);
        setMediumOptions(Array.isArray(med) ? med : []);
        setCampaignOptions(Array.isArray(cmp) ? cmp : []);
        setAllCountries(Array.isArray(flagsAndCodes) ? flagsAndCodes : []);
        const fields = Array.isArray(cf) ? cf : [];
        setCustomFields(fields);
        setCustomFieldValues(
          fields.reduce((acc, field) => {
            const id = customFieldId(field);
            const type = customFieldType(field);
            const defaultValue = field?.DefaultValue ?? field?.defaultValue ?? '';
            if (type === 'checkbox') {
              acc[id] = String(defaultValue) === '1' || String(defaultValue).toLowerCase() === 'true';
            } else if (type === 'list') {
              acc[id] = String(defaultValue)
                .split(',')
                .map((item) => item.trim())
                .filter(Boolean);
            } else {
              acc[id] = String(defaultValue ?? '');
            }
            return acc;
          }, {})
        );
        setFollowupStatuses(
          Array.isArray(fu)
            ? fu.map((f) => ({
              Code: String(f?.Code ?? f?.code ?? f?.Value ?? f?.value ?? ''),
              Text: f?.Text ?? f?.text ?? f?.FollowupStatus ?? f?.Name ?? '',
            })).filter((f) => f.Code || f.Text)
            : []
        );
      } catch (err) {
        console.warn('AddLeadForm fetch error', err);
      }
    })();

    return () => { alive = false; };
  }, []);

  // ─── country → state cascade ──────────────────────────────────────────────
  const handleCountryChange = async (e) => {
    const val = e.target.value;
    const found = countries.find(c => String(c.Id ?? c.id ?? '') === String(val));
    const countryText = found ? (found.Text ?? found.text ?? found.Name ?? '') : '';
    setForm(prev => ({ ...prev, countryId: val, country: countryText, stateId: '', state: '', cityId: '', city: '' }));
    setStates([]);
    setCities([]);
    if (val) {
      try {
        const st = await getStateListByCountryId({ Details: { CountryId: val } });
        setStates(Array.isArray(st) ? st : []);
      } catch (err) { console.warn(err); }
    }
  };

  // ─── state → city cascade ────────────────────────────────────────────────
  const handleStateChange = async (e) => {
    const val = e.target.value;
    const found = states.find(s => String(s.Id ?? s.id ?? '') === String(val));
    const stateText = found ? (found.Text ?? found.text ?? '') : '';
    setForm(prev => ({ ...prev, stateId: val, state: stateText, cityId: '', city: '' }));
    setCities([]);
    if (val) {
      try {
        const ct = await getCityListByStateId({ Details: { StateId: val } });
        setCities(Array.isArray(ct) ? ct : []);
      } catch (err) { console.warn(err); }
    }
  };

  const handleCityChange = (e) => {
    const val = e.target.value;
    const found = cities.find(c => String(c.Id ?? c.id ?? '') === String(val));
    const cityText = found ? (found.Text ?? found.text ?? found.Name ?? '') : '';
    setForm(prev => ({ ...prev, cityId: val, city: cityText }));
  };

  // ─── mobile helpers ───────────────────────────────────────────────────────
  const updateMobile = (idx, field, value) => {
    setForm(prev => {
      const m = [...prev.mobiles];
      let val = value;

      if (field === 'number') {
        val = value.replace(/\D/g, '');
        const code = m[idx].code;
        const config = COUNTRY_CODES.find(c => c.code === code);
        if (config && val.length > config.len) {
          val = val.slice(0, config.len);
        }
      } else if (field === 'code') {
        const config = COUNTRY_CODES.find(c => c.code === val);
        const maxLen = config ? config.len : 15;
        if (m[idx].number.length > maxLen) {
          m[idx].number = m[idx].number.slice(0, maxLen);
        }
      }

      m[idx] = { ...m[idx], [field]: val };
      return { ...prev, mobiles: m };
    });
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (countryPickerRef.current && !countryPickerRef.current.contains(e.target)) {
        setActiveCountryPickerIdx(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addMobile = () => {
    setForm(prev => prev.mobiles.length < 3
      ? { ...prev, mobiles: [...prev.mobiles, { code: '+91', number: '' }] }
      : prev
    );
  };
  const removeMobile = (idx) => {
    setForm(prev => ({ ...prev, mobiles: prev.mobiles.filter((_, i) => i !== idx) }));
  };

  // ─── email helpers ────────────────────────────────────────────────────────
  const updateEmail = (idx, value) => {
    setForm(prev => {
      const e = [...prev.emails];
      e[idx] = { address: value };
      return { ...prev, emails: e };
    });
  };
  const addEmail = () => {
    setForm(prev => prev.emails.length < 3
      ? { ...prev, emails: [...prev.emails, { address: '' }] }
      : prev);
  };
  const removeEmail = (idx) => {
    setForm(prev => ({ ...prev, emails: prev.emails.filter((_, i) => i !== idx) }));
  };

  // ─── source / option label helpers ───────────────────────────────────────
  const optLabel = (o) => o?.SrcmedCampName ?? o?.Name ?? o?.Text ?? '';
  const optId = (o) => o?.SrcmedCampID ?? o?.SrcmedCampId ?? o?.ID ?? '';
  // API returns CommonEntity with Code (raw FUPValue) and Text (display status)
  const fuLabel = (o) => o?.Text ?? o?.FollowupStatus ?? o?.Name ?? o?.label ?? '';
  const fuId = (o) => String(o?.Code ?? o?.code ?? o?.Value ?? o?.value ?? '');

  const followupUi = useMemo(() => {
    // Determine flags from the selected followup object first, then fallback to the CSV value
    let flag1 = 0;
    let flag2 = 0;

    const tryParseFromCsv = (val) => {
      if (!val) return null;
      const parts = String(val).split(',');
      if (parts.length >= 3) {
        return { f1: parseInt(parts[1], 10) || 0, f2: parseInt(parts[2], 10) || 0 };
      }
      return null;
    };

    const csv = tryParseFromCsv(form.followupStatusValue || (selectedFollowup && (selectedFollowup.Code ?? selectedFollowup.code ?? selectedFollowup.Value ?? selectedFollowup.value)));
    if (csv) {
      flag1 = csv.f1;
      flag2 = csv.f2;
    } else if (selectedFollowup) {
      flag1 = parseInt(selectedFollowup.Flag1 ?? selectedFollowup.flag1 ?? selectedFollowup.IsNextStatus ?? selectedFollowup.ShowNext ?? 0, 10) || 0;
      flag2 = parseInt(selectedFollowup.Flag2 ?? selectedFollowup.flag2 ?? selectedFollowup.IsConversion ?? selectedFollowup.ShowConversion ?? 0, 10) || 0;
    } else {
      flag1 = 0;
      flag2 = 0;
    }

    const text = fuLabel(selectedFollowup || {}).trim();
    if (text === 'Converted') {
      flag1 = 0; flag2 = 1;
    } else if (text === 'Call-Back') {
      flag1 = 1; flag2 = 0;
    } else if (text === 'Dead Lead') {
      flag1 = 0; flag2 = 0;
    }

    const base = {
      showAssign: false,
      showStatusDate: false,
      showRemarks: true,
      showProducts: false,
      showReassign: false,
      showDeal: false,
      showAmount: false,
      statusDateLabel: 'Next Status Date',
      useConversionDate: false,
    };

    // Next Status (Call-Back / Default)
    if (flag1 === 1 && flag2 === 0) {
      return {
        ...base,
        showAssign: true,
        showStatusDate: true,
        showProducts: true,
        showReassign: true,
        showDeal: true,
        statusDateLabel: 'Next Status Date',
        useConversionDate: false,
      };
    }

    // Dead Lead
    if (flag1 === 0 && flag2 === 0) {
      return base;
    }

    // Converted
    if (flag1 === 0 && flag2 === 1) {
      return {
        ...base,
        showAssign: true,
        showStatusDate: true,
        showProducts: true,
        showReassign: true, // Matches AddFollowupForm.jsx
        showAmount: true,
        statusDateLabel: 'Conversion Date',
        useConversionDate: true,
      };
    }

    // Both (Rare, but handled)
    if (flag1 === 1 && flag2 === 1) {
      return {
        ...base,
        showAssign: true,
        showStatusDate: true,
        showProducts: true,
        showReassign: true,
        showAmount: true,
        statusDateLabel: 'Next Status Date',
        useConversionDate: false,
      };
    }

    return base;
  }, [form.followupStatusValue, selectedFollowup]);

  const timeOptions = useMemo(() => {
    const opts = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 30) {
        const hh = h % 12 || 12;
        const ampm = h < 12 ? 'AM' : 'PM';
        const mm = String(m).padStart(2, '0');
        opts.push(`${String(hh).padStart(2, '0')}:${mm} ${ampm}`);
      }
    }
    return opts;
  }, []);

  const formatDateDisplay = (val) => {
    if (!val) return '';
    const dt = new Date(val);
    if (isNaN(dt.getTime())) return val;
    const dd = String(dt.getDate()).padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const m = months[dt.getMonth()] || '';
    const yyyy = dt.getFullYear();
    return `${dd}-${m}-${yyyy}`;
  };

  useEffect(() => {
    const handleOutside = (e) => {
      if (nextDateRef.current && !nextDateRef.current.contains(e.target)) setShowNextDate(false);
      if (nextTimeRef.current && !nextTimeRef.current.contains(e.target)) setShowNextTime(false);
      if (convDateRef.current && !convDateRef.current.contains(e.target)) setShowConvDate(false);
      if (convTimeRef.current && !convTimeRef.current.contains(e.target)) setShowConvTime(false);
      if (productsRef.current && !productsRef.current.contains(e.target)) setShowProductSuggestions(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  // Products search effect
  useEffect(() => {
    if (!productQuery.trim()) {
      setProductSuggestions([]);
      return;
    }

    if (productDebounceRef.current) clearTimeout(productDebounceRef.current);

    productDebounceRef.current = setTimeout(async () => {
      try {
        const { TokenId, userId } = getSession();
        const resp = await getProductsByName({
          Token: TokenId,
          Message: '',
          LoggedUserId: userId ?? 0,
          MAC_Address: '',
          IP_Address: '',
          Details: { stName: productQuery, stUserID: userId },
          BroadcastName: '',
        });
        const list = Array.isArray(resp) ? resp : (resp?.Details && Array.isArray(resp.Details) ? resp.Details : []);
        setProductSuggestions(list.map(i => ({ id: i.Id || i.Value || '', text: i.Text || i.Name || '' })));
      } catch (err) {
        console.warn('Product search failed', err);
      }
    }, 400);

    return () => { if (productDebounceRef.current) clearTimeout(productDebounceRef.current); };
  }, [productQuery]);

  const selectProduct = (p) => {
    if (!selectedProducts.find(item => item.id === p.id)) {
      const newSelected = [...selectedProducts, p];
      setSelectedProducts(newSelected);
      setForm(prev => ({ ...prev, products: newSelected.map(x => x.text).join(', ') }));
    }
    setProductQuery('');
    setShowProductSuggestions(false);
  };

  const removeProduct = (id) => {
    setSelectedProducts(prev => {
      const newSelected = prev.filter(p => p.id !== id);
      setForm(f => ({ ...f, products: newSelected.map(x => x.text).join(', ') }));
      return newSelected;
    });
  };


  // ─── load users when Assign To / Deal / Collaborator section is visible ────
  useEffect(() => {
    let alive = true;

    if (!followupUi.showAssign && !followupUi.showDeal && !showCollaborator) return () => { alive = false; };

    const { TokenId, userId, parentId } = getSession();

    (async () => {
      try {
        const users = await getUserListByUserId({
          Token: TokenId,
          Message: '',
          LoggedUserId: userId ?? 0,
          MAC_Address: '',
          IP_Address: '',
          Details: { UserId: userId || 0 },
          BroadcastName: '',
        });

        if (!alive) return;
        setUserOptions(
          Array.isArray(users)
            ? users.map((u) => ({
              Code: String(u?.Code ?? u?.code ?? u?.UserId ?? u?.userId ?? u?.Id ?? u?.id ?? u?.ID ?? u?.Value ?? u?.value ?? ''),
              Text: u?.Text ?? u?.text ?? u?.UserName ?? u?.userName ?? u?.Name ?? u?.name ?? '',
            })).filter((u) => u.Code || u.Text)
            : []
        );
      } catch (err) {
        console.warn('AddLeadForm getUserListByUserId fetch error', err);
      }
    })();

    return () => { alive = false; };
  }, [followupUi.showAssign, followupUi.showDeal, showCollaborator]);
  // ─── load collaborator types when section opens ──────────────────────────
  useEffect(() => {
    if (!showCollaborator || collabTypeOptions.length > 0) return;
    const { TokenId, userId } = getSession();
    getCollaboratorTypes({
      Token: TokenId, Message: '', LoggedUserId: userId ?? 0,
      MAC_Address: '', IP_Address: '',
      Details: { UserID: userId || 0, ClNmae: '' },
      BroadcastName: '',
    }).then(list => {
      setCollabTypeOptions(
        Array.isArray(list)
          ? list.map(t => ({
            id: String(t?.Id ?? t?.id ?? t?.Code ?? t?.code ?? ''),
            name: t?.TypeName ?? t?.type_name ?? t?.typeName ?? t?.Text ?? t?.text ?? t?.Name ?? t?.name ?? '',
          })).filter(t => t.id || t.name)
          : []
      );
    }).catch(err => console.warn('collabType fetch error', err));
  }, [showCollaborator]);

  // ─── load teams when type changes and mode = team ─────────────────────────
  useEffect(() => {
    if (!showCollaborator || collabMode !== 'team' || !collabSelectedType.id) {
      setCollabTeamOptions([]);
      return;
    }
    const { TokenId, userId } = getSession();
    getTeamsByTypeId({
      Token: TokenId, Message: '', LoggedUserId: userId ?? 0,
      MAC_Address: '', IP_Address: '',
      Details: { UserID: userId || 0, CollTeamName: '' },
      BroadcastName: '',
    }).then(list => {
      setCollabTeamOptions(
        Array.isArray(list)
          ? list.map(t => ({
            id: String(t?.Id ?? t?.id ?? t?.Code ?? t?.code ?? ''),
            name: t?.TeamName ?? t?.team_name ?? t?.teamName ?? t?.Text ?? t?.text ?? t?.Name ?? t?.name ?? '',
          })).filter(t => t.id || t.name)
          : []
      );
    }).catch(err => console.warn('collabTeam fetch error', err));
  }, [showCollaborator, collabMode, collabSelectedType.id]);

  // ─── add collaborator row ─────────────────────────────────────────────────
  const handleAddCollaborator = () => {
    if (!collabSelectedType.id || !collabSelectedValue.id) return;
    const exists = collaborators.some(
      c => c.typeId === collabSelectedType.id &&
        c.mode === collabMode &&
        c.selectedId === collabSelectedValue.id
    );
    if (exists) return;
    setCollaborators(prev => [...prev, {
      typeId: collabSelectedType.id,
      typeName: collabSelectedType.name,
      mode: collabMode,
      selectedId: collabSelectedValue.id,
      selectedName: collabSelectedValue.name,
    }]);
    setCollabSelectedValue({ id: '', name: '' });
  };

  const handleRemoveCollaborator = (idx) =>
    setCollaborators(prev => prev.filter((_, i) => i !== idx));
  // ─── load pipeline options when deal section becomes visible ───────────
  useEffect(() => {
    let alive = true;

    if (!followupUi.showDeal) return () => { alive = false; };

    const { TokenId, userId, parentId } = getSession();

    (async () => {
      try {
        const pipelines = await getDealPipeline({
          Token: TokenId,
          Message: '',
          LoggedUserId: userId ?? 0,
          MAC_Address: '',
          IP_Address: '',
          Details: parentId || 0,
          BroadcastName: '',
        });

        if (!alive) return;

        // API returns CommonEntity: { Id, Code (= ID string), Text (= Name) }
        setDealPipelineOptions(
          Array.isArray(pipelines)
            ? pipelines
              .map((p) => ({
                Code: String(p?.Code ?? p?.code ?? p?.Id ?? p?.id ?? ''),
                Text: p?.Text ?? p?.text ?? p?.Name ?? p?.name ?? '',
              }))
              .filter((p) => p.Code || p.Text)
            : []
        );
      } catch (err) {
        console.warn('AddLeadForm pipeline fetch error', err);
      }
    })();

    return () => { alive = false; };
  }, [followupUi.showDeal]);

  // ─── load stage options whenever the selected pipeline changes ───────────
  useEffect(() => {
    let alive = true;

    if (!form.dealPipeline) {
      setDealStageOptions([]);
      return () => { alive = false; };
    }

    const { TokenId, userId } = getSession();

    (async () => {
      try {
        // Details must be a long (pipelineId) — backend: JsonConvert.DeserializeObject<long>(Details.ToString())
        const stages = await getDealPipelineStage({
          Token: TokenId,
          Message: '',
          LoggedUserId: userId ?? 0,
          MAC_Address: '',
          IP_Address: '',
          Details: Number(form.dealPipeline) || 0,
          BroadcastName: '',
        });

        if (!alive) return;

        // API returns PipelineStage: { Id, Name, Probability, IsDefault, Sequence }
        setDealStageOptions(Array.isArray(stages) ? stages : []);
      } catch (err) {
        console.warn('AddLeadForm stage fetch error', err);
      }
    })();

    return () => { alive = false; };
  }, [form.dealPipeline]);

  // ─── submit ───────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.name.trim()) {
      Swal.fire({ icon: 'warning', title: 'Name required', text: 'Please enter the lead name.' });
      return;
    }

    const nameRegex = /^[a-zA-Z0-9._ ]+$/;
    if (!nameRegex.test(form.name)) {
      Swal.fire({ icon: 'error', title: 'Invalid Name', text: 'Name can only contain letters, numbers, dots, and underscores.' });
      return;
    }

    if (!form.sourceId || !form.mediumId) {
      Swal.fire({ icon: 'warning', title: 'Required', text: 'Please select source and medium' });
      return;
    }

    // Mobile validation
    const updatedMobiles = [...form.mobiles];

    // Update state so the payload build below uses fresh data
    setForm(prev => ({ ...prev, mobiles: updatedMobiles }));

    // Check for duplicate mobile numbers
    const enteredMobiles = updatedMobiles
      .map(mob => `${mob.code || ''}${mob.number || ''}`.trim())
      .filter(val => val.length > 0);
    const uniqueMobiles = new Set(enteredMobiles);
    if (uniqueMobiles.size !== enteredMobiles.length) {
      Swal.fire({ icon: 'warning', title: 'Duplicate Mobile', text: 'All mobile numbers must be different.' });
      return;
    }

    for (let i = 0; i < updatedMobiles.length; i++) {
      const mob = updatedMobiles[i];
      const num = String(mob.number || '').trim();
      const code = String(mob.code || '').trim();

      if (i === 0 && !num) {
        Swal.fire({ icon: 'warning', title: 'Mobile required', text: 'Please enter primary mobile number.' });
        return;
      }

      if (num) {
        if (!code) {
          Swal.fire({ icon: 'warning', title: 'Country Code Required', text: `Please select a country code for Mobile ${i + 1}.` });
          return;
        }

        if (!/^\d+$/.test(num)) {
          Swal.fire({ icon: 'warning', title: 'Invalid Mobile', text: `Mobile ${i + 1} must contain only digits.` });
          return;
        }

        const config = COUNTRY_CODES.find(c => c.code === code);
        if (config && num.length !== config.len) {
          Swal.fire({
            icon: 'warning',
            title: 'Invalid Mobile Length',
            text: `${config.label} mobile number must be exactly ${config.len} digits.`
          });
          return;
        }
      }
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (let i = 0; i < form.emails.length; i++) {
      const email = String(form.emails[i]?.address || '').trim();
      if (email && !emailRegex.test(email)) {
        Swal.fire({ icon: 'warning', title: 'Invalid Email', text: `Email ${i + 1} format is invalid.` });
        return;
      }
    }

    // Check for duplicate email addresses
    const enteredEmails = form.emails
      .map(e => String(e?.address || '').trim().toLowerCase())
      .filter(Boolean);
    const uniqueEmails = new Set(enteredEmails);
    if (uniqueEmails.size !== enteredEmails.length) {
      Swal.fire({ icon: 'warning', title: 'Duplicate Email', text: 'All email addresses must be different.' });
      return;
    }


    const customValidationError = validateMandatoryCustomFields();
    if (customValidationError) {
      Swal.fire({ icon: 'warning', title: 'Custom field required', text: customValidationError });
      return;
    }

    if (showCollaborator && collaborators.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Collaborator required', text: 'Please add at least one collaborator.' });
      return;
    }

    const { TokenId, userId, parentId } = getSession();
    const primaryMobile = updatedMobiles[0]?.number || '';

    const nextStatusDateValue = followupUi.showStatusDate
      ? (followupUi.useConversionDate ? form.conversionDate : form.nextStatusDate)
      : '';
    const nextStatusTimeValue = followupUi.showStatusDate
      ? (followupUi.useConversionDate ? form.conversionTime : form.nextStatusTime)
      : '';

    const assignedTo = Number(form.assignTo || form.salesOwner || 0) || 0;
    let uploadedFileName = personImageName || '';

    const typeIds = [];
    const teamIds = [];
    const userIds = [];

    if (showCollaborator) {
      collaborators.forEach((c) => {
        typeIds.push(String(c.typeId || 0));
        if (String(c.mode).toLowerCase() === 'team') {
          teamIds.push(String(c.selectedId || 0));
          userIds.push('0');
        } else {
          teamIds.push('0');
          userIds.push(String(c.selectedId || 0));
        }
      });
    }

    const details = {
      ParentId: parentId || 0,
      PersonName: form.name.trim(),
      PersonImage: uploadedFileName,
      CompanyName: form.company,
      MobileNo1: primaryMobile,
      MobileNo2: updatedMobiles[1]?.number || '',
      MobileNo3: updatedMobiles[2]?.number || '',
      EmailId1: form.emails[0]?.address || '',
      EmailId2: form.emails[1]?.address || '',
      EmailId3: form.emails[2]?.address || '',
      City: form.city,
      State: form.state,
      Country: form.country,
      CountryCode1: updatedMobiles[0]?.code || '',
      CountryCode2: updatedMobiles[1]?.code || '',
      CountryCode3: updatedMobiles[2]?.code || '',
      PinCode: form.pinCode,
      ResidentialAddress: form.residentialAddress,
      OfficeAddress: form.officeAddress,
      SourceName: form.sourceId,
      MediumName: form.mediumId,
      CampaignName: form.campaignId,
      Remarks: form.remarks,
      CreatedBy: userId || 0,
      AssignedTo: assignedTo || userId,
      FollowUpStatus: form.followupStatusValue,
      NextStatusDate: `${nextStatusDateValue || ''} ${nextStatusTimeValue || ''}`.trim(),
      Products: followupUi.showProducts ? (selectedProducts.length > 0 ? selectedProducts.map(p => p.text).join(', ') : form.products) : '',
      AmountPaid: Number(followupUi.showAmount ? form.amountPaid || 0 : 0),
      FollowUpRemarks: form.remarks,
      NotifyBySMS: false,
      NotifyByEmail: false,
      IsReAssign: followupUi.showReassign ? !!form.reAssign : false,
      WhereLatitude: '',
      WhereLongitude: '',
      SmsScheduelDateTime: '',
      EmailScheduelDateTime: '',
      LeadTag: (Array.isArray(tagsArr) ? tagsArr.join(',') : (form.tags || '')),
      lstCustomeLeadFeild: customFields.map((field) => {
        const id = customFieldId(field);
        const type = customFieldType(field);
        const rawValue = customFieldValues[id];
        return {
          LeadId: 0,
          EnquiryId: 0,
          FieldId: Number(id) || 0,
          FieldName: customFieldName(field),
          FieldValue: Array.isArray(rawValue)
            ? rawValue.join(',')
            : type === 'checkbox'
              ? (!!rawValue ? '1' : '0')
              : (rawValue ?? ''),
        };
      }),
      TypeIds: showCollaborator ? typeIds.join(',') : '',
      TeamIds: showCollaborator ? teamIds.join(',') : '',
      UserIds: showCollaborator ? userIds.join(',') : '',
      DeletedTypeIds: '',
      DeletedTeamIds: '',
      DeletedUserIds: '',
    };

    const payload = {
      Token: TokenId,
      Message: '',
      LoggedUserId: userId ?? 0,
      MAC_Address: '',
      IP_Address: '',
      Details: details,
      BroadcastName: '',
    };

    try {
      setIsSaving(true);

      if (personImageFromLibrary && selectedMedia) {
        // selected from media library; use returned URL directly
        uploadedFileName = selectedMedia.url || selectedMedia.name || personImageName;
        payload.Details.PersonImage = uploadedFileName;
      } else if (personImageFile && personImageName) {
        // upload local file to docs host; pass userId so folder can be created per-user
        const uploadResp = await uploadLeadProfilePhoto(personImageFile, personImageName, userId || '');
        if (!(uploadResp?.Status === 1 || uploadResp?.status === 1)) {
          Swal.fire({ icon: 'error', title: 'Upload failed', text: uploadResp?.Message || 'Failed to upload profile image.' });
          return;
        }
        uploadedFileName = uploadResp?.FileName || personImageName;
        payload.Details.PersonImage = uploadedFileName;
      }

      const resp = await addLeadWithDependencies(payload);

      // Parse Details if it's a JSON string
      let detailsObj = null;
      if (typeof resp?.Details === 'string' && resp.Details.trim().startsWith('{')) {
        try {
          detailsObj = JSON.parse(resp.Details);
        } catch (e) {
          console.warn('Failed to parse resp.Details', e);
        }
      }

      const ok = resp && (
        resp.Status === 1 || resp.status === 1 ||
        (typeof resp.Details === 'string' && resp.Details.toLowerCase().includes('success')) ||
        (typeof resp.Message === 'string' && resp.Message.toLowerCase().includes('success'))
      );

      if (ok) {
        // Special check: Status 1 but Details contains LeadId: 0 (duplicate lead)
        if (detailsObj && detailsObj.LeadId === 0) {
          Swal.fire({ icon: 'warning', title: 'Already Exists', text: detailsObj.Msg || 'Lead already exists.' });
          return;
        }

        const successMsg = resp?.Message || (typeof resp?.Details === 'string' ? resp.Details : '') || 'Lead added successfully.';
        Swal.fire({ icon: 'success', title: 'Saved', text: 'Lead added successfully.' });
        // Swal.fire({ icon: 'success', title: 'Saved', text: successMsg });
        setForm(emptyForm());
        setSelectedProducts([]);
        setProductQuery('');
        setPersonImageFile(null);
        setPersonImageName('');
        setPersonImagePreview('https://kit19.com/assets/custom/img/img_avatar1.png');
        if (typeof onSaved === 'function') onSaved(resp);
        if (typeof onClose === 'function') onClose();
      } else {
        const errMsg = resp?.Message || (typeof resp?.Details === 'string' ? resp.Details : '') || 'Failed to save lead.';
        Swal.fire({ icon: 'error', title: 'Error', text: errMsg });
      }
    } catch (err) {
      console.error('AddLeadForm save error', err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'An error occurred while saving.' });
    } finally {
      setIsSaving(false);
    }
  };

  // ─── shared input classes ─────────────────────────────────────────────────
  const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500';
  const selectCls = `${inputCls} bg-white`;

  // Tag helpers
  useEffect(() => {
    try {
      const initial = String(form.tags || '');
      const arr = initial.split(',').map(s => s.trim()).filter(Boolean);
      setTagsArr(arr);
    } catch (err) { setTagsArr([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setForm(prev => ({ ...prev, tags: Array.isArray(tagsArr) ? tagsArr.join(',') : '' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagsArr]);

  const addTagsFromString = (str) => {
    if (!str) return;
    const parts = String(str).split(',').map(s => s.trim()).filter(Boolean);
    if (!parts.length) return;
    setTagsArr(prev => {
      const set = new Set(prev.map(p => p.toLowerCase()));
      const merged = [...prev];
      parts.forEach(p => {
        if (!set.has(p.toLowerCase())) {
          merged.push(p);
          set.add(p.toLowerCase());
        }
      });
      return merged;
    });
    setTagInput('');
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab' || e.key === ',') {
      e.preventDefault();
      addTagsFromString(tagInput || '');
    }
  };

  const handleTagPaste = (e) => {
    const text = (e.clipboardData || window.clipboardData).getData('text') || '';
    if (text && text.includes(',')) {
      e.preventDefault();
      addTagsFromString(text);
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 overflow-hidden">
      <style>{`
        .iti { width: 100% !important; }
        .iti__selected-flag { 
          display: flex !important;
          align-items: center !important;
          padding-left: 12px !important;
          padding-right: 12px !important;
          background: #f8f9fa !important;
          border-right: 1px solid #ddd !important;
          border-radius: 6px 0 0 6px !important;
        }
        .iti__flag {
          margin-right: 8px !important;
        }
        .iti__selected-dial-code { 
          color: #333 !important;
          font-weight: 500 !important;
          margin-left: 2px !important;
          font-size: 14px !important;
        }
        .iti--separate-dial-code .iti__selected-flag {
          width: auto !important;
        }
      `}</style>

      {/* Drawer Header */}
      <div className="add-lead-drawer-header">
        <div className="drawer-header-left">
          <button onClick={onClose} className="back-link-modern">
            <ChevronLeft size={18} />
            Back to Lead
          </button>
        </div>
        <div className="drawer-header-actions">
          <button onClick={onClose} className="drawer-header-btn close-btn">
            <X size={20} />
          </button>
        </div>
      </div>

      <div className="add-lead-drawer-content scrollbar-thin">
        <div className="drawer-inner-container">
          {/* ── Basic section ─────────────────────────────────────────────────── */}
          <div className="drawer-section premium-card">
            <div className="drawer-section-header">
              Basic <span className="text-red-500">*</span>
            </div>
            <div className="drawer-section-body">

        <div className="flex items-start gap-5 mb-4">
          {/* Avatar */}
          <div className="flex-shrink-0">
            <div
              className="w-20 h-20 rounded-full overflow-hidden bg-gray-100 border border-gray-200 cursor-pointer"
              title="Click to open Media Library"
              onClick={() => setShowMediaLibrary(true)}
            >
              <img
                src={personImagePreview}
                alt="Avatar"
                className="w-full h-full object-cover"
              />
            </div>
            {personImageName ? (
              <p className="mt-1 text-[11px] text-gray-500 break-all">{personImageName}</p>
            ) : null}
            {showMediaLibrary && (
              <MediaLibrary
                show={showMediaLibrary}
                onclose={() => setShowMediaLibrary(false)}
                onSelectMedia={(media) => {
                  if (!media) return;
                  setPersonImageFile(null);
                  setPersonImageName(media.name || '');
                  setPersonImagePreview(media.url);
                  setPersonImageFromLibrary(true);
                  setSelectedMedia(media);
                  setShowMediaLibrary(false);
                }}
                filterOption={'Images'}
              />
            )}
          </div>

          {/* Name */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
              className={inputCls}
              placeholder="Name (Required)"
            />
          </div>
        </div>

        {/* Mobiles */}
        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">Mobile</label>
          {form.mobiles.map((mob, idx) => (
            <div key={idx} className="flex items-center gap-2 mb-2">
              <div className={`flex-1 relative flex border border-gray-300 rounded focus-within:ring-2 focus-within:ring-blue-500 bg-white`}>
                <div className="relative border-r border-gray-300 bg-gray-50 flex items-center shrink-0" ref={activeCountryPickerIdx === idx ? countryPickerRef : null}>
                  <div 
                    className="flex items-center px-2 py-2 cursor-pointer h-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveCountryPickerIdx(activeCountryPickerIdx === idx ? null : idx);
                      setCountrySearchTerm('');
                    }}
                  >
                    {mob.code ? (
                      allCountries.find(c => c.dialCode === mob.code) ? (
                        <>
                          <img src={allCountries.find(c => c.dialCode === mob.code).flag} alt="" className="w-5 h-4 object-cover mr-1" />
                          <span className="text-sm">{mob.code}</span>
                        </>
                      ) : (
                        <span className="text-sm">{mob.code}</span>
                      )
                    ) : (
                      <>
                        <img src="https://flagcdn.com/w20/in.png" alt="IN" className="w-5 h-4 object-cover mr-1" />
                        <span className="text-sm">+91</span>
                      </>
                    )}
                    <ChevronDown size={14} className="ml-1 text-gray-500" />
                  </div>
                  {activeCountryPickerIdx === idx && (
                    <div className="absolute left-0 top-full mt-1 w-64 bg-white border border-gray-200 rounded shadow-lg z-50 max-h-60 flex flex-col">
                      <div className="p-2 border-b border-gray-100 sticky top-0 bg-white z-10">
                        <input
                          type="text"
                          className="w-full text-sm px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Search country..."
                          value={countrySearchTerm}
                          onChange={(e) => setCountrySearchTerm(e.target.value)}
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div className="overflow-y-auto overflow-x-hidden">
                        {allCountries
                          .filter(c => 
                            c.name.toLowerCase().includes(countrySearchTerm.toLowerCase()) || 
                            c.dialCode.includes(countrySearchTerm)
                          )
                          .map((country, cIdx) => (
                            <div 
                              key={`${country.code}-${cIdx}`} 
                              className="flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer"
                              onClick={() => {
                                updateMobile(idx, 'code', country.dialCode);
                                setActiveCountryPickerIdx(null);
                                setCountrySearchTerm('');
                              }}
                            >
                              <img src={country.flag} alt="" className="w-5 h-4 object-cover mr-2 shrink-0" />
                              <span className="text-sm text-gray-700 whitespace-nowrap truncate">{country.name} ({country.dialCode})</span>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  )}
                </div>
                <input
                  type="tel"
                  value={mob.number}
                  onChange={(e) => updateMobile(idx, 'number', e.target.value)}
                  className="flex-1 w-full bg-transparent px-3 py-2 text-sm focus:outline-none"
                  placeholder={`Mobile ${idx + 1}${idx === 0 ? ' (Required)' : ''}`}
                />
              </div>
              {idx === 0 && form.mobiles.length < 3 && (
                <button type="button" onClick={addMobile} className="text-green-600 hover:text-green-700" title="Add mobile">
                  <Plus className="w-4 h-4" />
                </button>
              )}
              {idx > 0 && (
                <button type="button" onClick={() => removeMobile(idx)} className="text-red-500 hover:text-red-600" title="Remove">
                  <Minus className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Emails */}
        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          {form.emails.map((em, idx) => (
            <div key={idx} className="flex items-center gap-2 mb-2">
              <input
                type="email"
                value={em.address}
                onChange={e => updateEmail(idx, e.target.value)}
                className={`flex-1 ${inputCls}`}
                placeholder={`Email ${idx + 1}${idx === 0 ? ' (Required)' : ''}`}
              />
              {idx === 0 && form.emails.length < 3 && (
                <button type="button" onClick={addEmail} className="text-green-600 hover:text-green-700" title="Add email">
                  <Plus className="w-4 h-4" />
                </button>
              )}
              {idx > 0 && (
                <button type="button" onClick={() => removeEmail(idx)} className="text-red-500 hover:text-red-600" title="Remove">
                  <Minus className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Source */}
        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">Source <span className="text-red-500">*</span></label>
          <select
            value={form.sourceId}
            onChange={e => {
              const id = e.target.value;
              const found = sourceOptions.find(s => String(optId(s)) === String(id));
              setForm(prev => ({ ...prev, sourceId: id, source: found ? optLabel(found) : '' }));
            }}
            className={selectCls}
          >
            <option value="">Select source (required)</option>
            {sourceOptions.map((s, i) => (
              <option key={i} value={optId(s)}>{optLabel(s)}</option>
            ))}
          </select>
        </div>

        {customFields.length > 0 && (
          <div className="mb-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {customFields.map((field, index) => {
                const id = customFieldId(field);
                const label = customFieldName(field);
                const type = customFieldType(field);
                const choices = customFieldChoices(field);
                const value = customFieldValues[id];
                const required = isCustomFieldMandatory(field);
                const minValue = field?.MinValue ?? field?.minvalue ?? '';
                const maxValue = field?.MaxValue ?? field?.maxvalue ?? '';
                const placeholder = [
                  label,
                  minValue || maxValue
                    ? `(${minValue ? `Min ${minValue}` : ''}${minValue && maxValue ? ', ' : ''}${maxValue ? `Max ${maxValue}` : ''})`
                    : ''
                ].filter(Boolean).join(' ');

                return (
                  <div key={`${id}-${index}`} className={type === 'multiline' || type === 'list' ? 'md:col-span-2' : ''}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {label}{required ? <span className="text-red-500">*</span> : null}
                    </label>

                    {type === 'date' ? (
                      <input
                        type="date"
                        value={String(value ?? '')}
                        onChange={e => updateCustomFieldValue(field, e.target.value)}
                        className={inputCls}
                      />
                    ) : type === 'multiline' ? (
                      <textarea
                        rows={3}
                        value={String(value ?? '')}
                        onChange={e => updateCustomFieldValue(field, e.target.value)}
                        className={`${inputCls} resize-none`}
                        placeholder={placeholder}
                      />
                    ) : type === 'numeric' ? (
                      <input
                        type="number"
                        value={String(value ?? '')}
                        min={minValue !== '' ? Number(minValue) : undefined}
                        max={maxValue !== '' ? Number(maxValue) : undefined}
                        onChange={e => updateCustomFieldValue(field, e.target.value)}
                        className={inputCls}
                        placeholder={placeholder}
                      />
                    ) : type === 'checkbox' ? (
                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={!!value}
                          onChange={e => updateCustomFieldValue(field, e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span>{label}</span>
                      </label>
                    ) : type === 'radio' ? (
                      <div className="flex flex-wrap gap-4">
                        {choices.map((choice) => (
                          <label key={choice} className="flex items-center gap-1 text-sm text-gray-700">
                            <input
                              type="radio"
                              name={`custom-radio-${id}`}
                              checked={String(value ?? '') === choice}
                              onChange={() => updateCustomFieldValue(field, choice)}
                              className="text-blue-600"
                            />
                            <span>{choice}</span>
                          </label>
                        ))}
                      </div>
                    ) : type === 'select' ? (
                      <select
                        value={String(value ?? '')}
                        onChange={e => updateCustomFieldValue(field, e.target.value)}
                        className={selectCls}
                      >
                        <option value="">-- Select Option --</option>
                        {choices.map((choice) => (
                          <option key={choice} value={choice}>{choice}</option>
                        ))}
                      </select>
                    ) : type === 'list' ? (
                      <select
                        multiple
                        value={Array.isArray(value) ? value : []}
                        onChange={e => updateCustomFieldValue(field, Array.from(e.target.selectedOptions).map(o => o.value))}
                        className={selectCls}
                      >
                        {choices.map((choice) => (
                          <option key={choice} value={choice}>{choice}</option>
                        ))}
                      </select>
                    ) : type === 'expression' ? (
                      <input
                        type="text"
                        value={String(value ?? '')}
                        readOnly
                        className={`${inputCls} bg-gray-100`}
                        placeholder={placeholder}
                      />
                    ) : (
                      <input
                        type="text"
                        value={String(value ?? '')}
                        onChange={e => updateCustomFieldValue(field, e.target.value)}
                        className={inputCls}
                        placeholder={placeholder}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Followup Status */}
        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">Followup Status</label>
          <select
            value={form.followupStatusValue}
            onChange={e => {
              const selectedId = e.target.value;
              const selected = followupStatuses.find(f => fuId(f) === selectedId);
              setForm(prev => ({
                ...prev,
                followupStatusValue: selectedId,
                followupStatus: selected ? fuLabel(selected) : '',
              }));
              setSelectedFollowup(selected || null);
            }}
            className={selectCls}
          >
            <option value="">Select followup status</option>
            {followupStatuses.map((f, i) => (
              <option key={i} value={fuId(f)}>{fuLabel(f)}</option>
            ))}
          </select>
        </div>

        {followupUi.showAssign && (
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
            <select
              value={form.assignTo}
              onChange={e => setForm(prev => ({ ...prev, assignTo: e.target.value }))}
              className={selectCls}
            >
              <option value="">Select user</option>
              {userOptions.map((u, i) => (
                <option key={i} value={u.Code}>{u.Text}</option>
              ))}
            </select>
          </div>
        )}

        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">Initial Remarks</label>
          <textarea
            value={form.remarks}
            onChange={e => setForm(prev => ({ ...prev, remarks: e.target.value }))}
            rows={3}
            className={`${inputCls} resize-none`}
            placeholder="Initial Remarks"
          />
        </div>

        {followupUi.showStatusDate && (
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="relative" ref={followupUi.useConversionDate ? convDateRef : nextDateRef}>
              <label className="block text-sm font-medium text-gray-700 mb-1">{followupUi.statusDateLabel}</label>
              <div
                className={`${inputCls} flex items-center justify-between cursor-pointer bg-white`}
                onClick={() => followupUi.useConversionDate ? setShowConvDate(!showConvDate) : setShowNextDate(!showNextDate)}
              >
                <span>{formatDateDisplay(followupUi.useConversionDate ? form.conversionDate : form.nextStatusDate) || 'Select date'}</span>
                <Calendar className="w-4 h-4 text-gray-400" />
              </div>
              {(followupUi.useConversionDate ? showConvDate : showNextDate) && (
                <div className="absolute z-50 left-0 top-full mt-1 bg-white border border-gray-200 rounded shadow p-2">
                  <input
                    type="date"
                    value={followupUi.useConversionDate ? form.conversionDate : form.nextStatusDate}
                    onChange={e => {
                      setForm(prev => followupUi.useConversionDate ? { ...prev, conversionDate: e.target.value } : { ...prev, nextStatusDate: e.target.value });
                      followupUi.useConversionDate ? setShowConvDate(false) : setShowNextDate(false);
                    }}
                    className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>
            <div className="relative" ref={followupUi.useConversionDate ? convTimeRef : nextTimeRef}>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
              <div
                className={`${inputCls} flex items-center justify-between cursor-pointer bg-white`}
                onClick={() => followupUi.useConversionDate ? setShowConvTime(!showConvTime) : setShowNextTime(!showNextTime)}
              >
                <span>{(followupUi.useConversionDate ? form.conversionTime : form.nextStatusTime) || 'Select time'}</span>
                <Clock className="w-4 h-4 text-gray-400" />
              </div>
              {(followupUi.useConversionDate ? showConvTime : showNextTime) && (
                <div className="absolute z-50 right-0 top-full mt-1 bg-white border border-gray-200 rounded shadow max-h-48 overflow-y-auto w-40">
                  {timeOptions.map(t => (
                    <div
                      key={t}
                      onClick={() => {
                        setForm(prev => followupUi.useConversionDate ? { ...prev, conversionTime: t } : { ...prev, nextStatusTime: t });
                        followupUi.useConversionDate ? setShowConvTime(false) : setShowNextTime(false);
                      }}
                      className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-sm text-gray-700"
                    >
                      {t}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {followupUi.showAmount && (
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount Paid</label>
            <input
              type="text"
              value={form.amountPaid}
              onChange={e => setForm(prev => ({ ...prev, amountPaid: e.target.value.replace(/\D/g, '') }))}
              className={inputCls}
              placeholder="Amount Paid"
            />
          </div>
        )}

        {followupUi.showProducts && (
          <div className="mb-3 relative" ref={productsRef}>
            <label className="block text-sm font-medium text-gray-700 mb-1">Choose Products</label>
            <input
              type="text"
              value={productQuery}
              onChange={e => {
                setProductQuery(e.target.value);
                setShowProductSuggestions(true);
                if (selectedProducts.length === 0) {
                  setForm(prev => ({ ...prev, products: e.target.value }));
                }
              }}
              onFocus={() => setShowProductSuggestions(true)}
              className={inputCls}
              placeholder="Enter product"
            />
            {showProductSuggestions && productSuggestions.length > 0 && (
              <div className="absolute left-0 right-0 z-50 mt-1 bg-white border border-gray-200 rounded shadow max-h-40 overflow-auto">
                {productSuggestions.map(p => (
                  <div key={p.id} onMouseDown={(e) => { e.preventDefault(); selectProduct(p); }} className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm">
                    {p.text}
                  </div>
                ))}
              </div>
            )}
            {selectedProducts.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {selectedProducts.map(p => (
                  <span
                    key={p.id}
                    className="inline-flex items-center gap-1.5 px-3 py-1 bg-white border border-gray-300 text-gray-700 rounded text-sm shadow-sm"
                  >
                    {p.text}
                    <button
                      type="button"
                      onClick={() => removeProduct(p.id)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}


        {followupUi.showReassign && (
          <div className="mb-3 flex items-center gap-2">
            <input
              id="reassign"
              type="checkbox"
              checked={!!form.reAssign}
              onChange={e => setForm(prev => ({ ...prev, reAssign: e.target.checked }))}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="reassign" className="text-sm text-gray-700">Followup Type: Re-Assign</label>
          </div>
        )}

        {followupUi.showDeal && (
          <div className="mb-3 p-3 border border-gray-200 rounded-md bg-gray-50">
            <p className="text-sm font-medium text-gray-700 mb-2">Add Deal</p>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                value={form.dealValue}
                onChange={e => setForm(prev => ({ ...prev, dealValue: e.target.value.replace(/\D/g, '') }))}
                className={inputCls}
                placeholder="Deal Value"
              />
              <input
                type="text"
                value={form.probability}
                onChange={e => setForm(prev => ({ ...prev, probability: e.target.value.replace(/\D/g, '') }))}
                className={inputCls}
                placeholder="Probability (%)"
              />
              <select
                value={form.dealPipeline}
                onChange={e => setForm(prev => ({ ...prev, dealPipeline: e.target.value, dealStage: '', probability: '' }))}
                className={selectCls}
              >
                <option value="">Deal Pipeline</option>
                {dealPipelineOptions.map((p, i) => (
                  <option key={i} value={p.Code}>{p.Text}</option>
                ))}
              </select>
              <select
                value={form.dealStage}
                onChange={e => {
                  const stageId = e.target.value;
                  const found = dealStageOptions.find(s => String(s.Id) === String(stageId));
                  setForm(prev => ({
                    ...prev,
                    dealStage: stageId,
                    probability: found?.Probability != null ? String(found.Probability) : prev.probability,
                  }));
                }}
                className={selectCls}
              >
                <option value="">Deal Stage</option>
                {dealStageOptions.map((s, i) => (
                  <option key={i} value={String(s.Id)}>{s.Name}</option>
                ))}
              </select>
              <select
                value={form.salesOwner}
                onChange={e => setForm(prev => ({ ...prev, salesOwner: e.target.value }))}
                className={selectCls}
              >
                <option value="">Sales Owner</option>
                {userOptions.map((u, i) => (
                  <option key={i} value={u.Code}>{u.Text}</option>
                ))}
              </select>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Estimated Closure Date</label>
                <input
                  type="date"
                  value={form.dealClosureDate}
                  onChange={e => setForm(prev => ({ ...prev, dealClosureDate: e.target.value }))}
                  className={inputCls}
                  placeholder="Closure Date"
                />
              </div>
            </div>
          </div>
        )}
            </div>
          </div>

          {/* ── Collaborator section ──────────────────────────────────────────── */}
          <div className="drawer-section premium-card">
            <div className="drawer-section-header">
              Collaborator
            </div>
            <div className="drawer-section-body">
              <label className="flex items-center gap-2 cursor-pointer select-none mb-3">
                <input
                  type="checkbox"
                  checked={showCollaborator}
                  onChange={e => {
                    setShowCollaborator(e.target.checked);
                    if (!e.target.checked) {
                      setCollaborators([]);
                      setCollabSelectedType({ id: '', name: '' });
                      setCollabSelectedValue({ id: '', name: '' });
                      setCollabMode('team');
                    }
                  }}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Add Collaborator</span>
              </label>

              {showCollaborator && (
          <div className="p-3 border border-gray-200 rounded-md bg-gray-50">
            {/* Controls row */}
            <div className="flex flex-wrap gap-3 items-start mb-3">
              {/* Type dropdown */}
              <select
                value={collabSelectedType.id}
                onChange={e => {
                  const found = collabTypeOptions.find(t => t.id === e.target.value);
                  setCollabSelectedType(found ?? { id: e.target.value, name: '' });
                  setCollabSelectedValue({ id: '', name: '' });
                }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 w-48"
              >
                <option value="">-- Select Type --</option>
                {collabTypeOptions.map((t, i) => (
                  <option key={i} value={t.id}>{t.name}</option>
                ))}
              </select>

              {/* Radio: Team / User */}
              <div className="flex gap-4 items-center">
                <label className="flex items-center gap-1 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name="collabAddSelectMode"
                    value="team"
                    checked={collabMode === 'team'}
                    onChange={() => {
                      setCollabMode('team');
                      setCollabSelectedValue({ id: '', name: '' });
                    }}
                    className="text-blue-600"
                  />
                  Select Team
                </label>
                <label className="flex items-center gap-1 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name="collabAddSelectMode"
                    value="user"
                    checked={collabMode === 'user'}
                    onChange={() => {
                      setCollabMode('user');
                      setCollabSelectedValue({ id: '', name: '' });
                    }}
                    className="text-blue-600"
                  />
                  Select User
                </label>
              </div>

              {/* Team or User dropdown */}
              {collabMode === 'team' ? (
                <select
                  value={collabSelectedValue.id}
                  onChange={e => {
                    const found = collabTeamOptions.find(t => t.id === e.target.value);
                    setCollabSelectedValue(found ?? { id: e.target.value, name: '' });
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 w-48"
                >
                  <option value="">-- Select Team --</option>
                  {collabTeamOptions.map((t, i) => (
                    <option key={i} value={t.id}>{t.name}</option>
                  ))}
                </select>
              ) : (
                <select
                  value={collabSelectedValue.id}
                  onChange={e => {
                    const found = userOptions.find(u => u.Code === e.target.value);
                    setCollabSelectedValue({ id: e.target.value, name: found?.Text ?? e.target.value });
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 w-48"
                >
                  <option value="">-- Select User --</option>
                  {userOptions.map((u, i) => (
                    <option key={i} value={u.Code}>{u.Text}</option>
                  ))}
                </select>
              )}

              {/* Add button */}
              <button
                type="button"
                onClick={handleAddCollaborator}
                disabled={!collabSelectedType.id || !collabSelectedValue.id}
                className="flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus className="w-3.5 h-3.5" /> Add
              </button>
            </div>

            {/* Collaborators table */}
            {collaborators.length > 0 && (
              <table className="w-full text-sm border border-gray-200 rounded-md overflow-hidden">
                <thead className="bg-gray-100">
                  <tr>
                    {['TYPE', 'MODE', 'SELECTED', 'ACTION'].map(h => (
                      <th key={h} className="px-3 py-2 text-left font-semibold text-gray-600 border-b border-gray-200 text-xs">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {collaborators.map((c, i) => (
                    <tr key={i} className="border-b border-gray-100 last:border-0 hover:bg-white">
                      <td className="px-3 py-2 text-gray-700">{c.typeName || c.typeId}</td>
                      <td className="px-3 py-2 text-gray-700 capitalize">{c.mode}</td>
                      <td className="px-3 py-2 text-gray-700">{c.selectedName || c.selectedId}</td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => handleRemoveCollaborator(i)}
                          className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-red-500 text-white hover:bg-red-600"
                        >
                          <Minus className="w-3 h-3" /> Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
              )}
            </div>
          </div>

          {/* ── Advance toggle ────────────────────────────────────────────────── */}
          <div className="px-1">
            <button
              type="button"
              onClick={() => setShowAdvance(v => !v)}
              className="flex items-center gap-1 text-sm font-medium text-green-600 hover:text-green-700 mb-4"
            >
              {showAdvance ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {showAdvance ? 'Hide Advance' : 'Advance'}
            </button>
          </div>

          {showAdvance && (
            <>
              {/* ── Lead Details ─────────────────────────────────────────────── */}
              <div className="drawer-section premium-card">
                <div className="drawer-section-header">
                  Lead Details
                </div>
                <div className="drawer-section-body">

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Medium</label>
                <select
                  value={form.mediumId}
                  onChange={e => {
                    const id = e.target.value;
                    const found = mediumOptions.find(m => String(optId(m)) === String(id));
                    setForm(prev => ({ ...prev, mediumId: id, medium: found ? optLabel(found) : '' }));
                  }}
                  className={selectCls}
                >
                  <option value="">Select medium</option>
                  {mediumOptions.map((m, i) => (
                    <option key={i} value={optId(m)}>{optLabel(m)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Campaign</label>
                <select
                  value={form.campaignId}
                  onChange={e => {
                    const id = e.target.value;
                    const found = campaignOptions.find(c => String(optId(c)) === String(id));
                    setForm(prev => ({ ...prev, campaignId: id, campaign: found ? optLabel(found) : '' }));
                  }}
                  className={selectCls}
                >
                  <option value="">Select campaign</option>
                  {campaignOptions.map((c, i) => (
                    <option key={i} value={optId(c)}>{optLabel(c)}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Lead Tags</label>
              <div className="flex items-center gap-2 flex-wrap p-2 border border-gray-300 rounded-md">
                {tagsArr.map((t, idx) => (
                  <span key={`${t}-${idx}`} className="flex items-center gap-2 bg-gray-100 text-sm text-gray-800 px-2 py-1 rounded-full">
                    <span>{t}</span>
                    <button type="button" onClick={() => setTagsArr(prev => prev.filter((_, i) => i !== idx))} className="text-gray-500 hover:text-gray-700">×</button>
                  </span>
                ))}

                <input
                  type="text"
                  value={tagInput}
                  onChange={e => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  onPaste={handleTagPaste}
                  className="flex-1 min-w-[160px] px-2 py-1 text-sm bg-transparent border-0 focus:outline-none"
                  placeholder="Enter tags (press comma/tab/enter)"
                />
              </div>
              <p className="mt-1 text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1">
                Press comma, tab, or enter to generate a tag name.
              </p>
            </div>
                </div>
              </div>

              {/* ── Company Details ───────────────────────────────────────────── */}
              <div className="drawer-section premium-card">
                <div className="drawer-section-header">
                  Company Details
                </div>
                <div className="drawer-section-body">

            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
              <input
                type="text"
                value={form.company}
                onChange={e => setForm(prev => ({ ...prev, company: e.target.value }))}
                className={inputCls}
                placeholder="Company"
              />
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <select value={form.countryId} onChange={handleCountryChange} className={selectCls}>
                  <option value="">Country</option>
                  {countries.map((c, i) => (
                    <option key={i} value={String(c.Id ?? c.id ?? '')}>{c.Text ?? c.text ?? c.Name ?? ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <select value={form.stateId} onChange={handleStateChange} className={selectCls}>
                  <option value="">State</option>
                  {states.map((s, i) => (
                    <option key={i} value={String(s.Id ?? s.id ?? '')}>{s.Text ?? s.text ?? ''}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <select value={form.cityId} onChange={handleCityChange} className={selectCls}>
                  <option value="">City</option>
                  {cities.map((c, i) => (
                    <option key={i} value={String(c.Id ?? c.id ?? '')}>{c.Text ?? c.text ?? c.Name ?? ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pin Code</label>
                <input
                  type="text"
                  value={form.pinCode}
                  onChange={e => setForm(prev => ({ ...prev, pinCode: e.target.value.replace(/\D/g, '') }))}
                  className={inputCls}
                  placeholder="Pin Code"
                  maxLength={10}
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Residential Address</label>
              <textarea
                value={form.residentialAddress}
                onChange={e => setForm(prev => ({ ...prev, residentialAddress: e.target.value }))}
                rows={2}
                className={`${inputCls} resize-none`}
                placeholder="Residential Address"
              />
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Official Address</label>
              <textarea
                value={form.officeAddress}
                onChange={e => setForm(prev => ({ ...prev, officeAddress: e.target.value }))}
                rows={2}
                className={`${inputCls} resize-none`}
                placeholder="Official Address"
              />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>

      {/* Drawer Footer */}
      <div className="drawer-footer-modern">
        <button
          type="button"
          onClick={onClose}
          className="drawer-cancel-btn-modern"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="drawer-save-btn-modern"
        >
          {isSaving ? 'Saving…' : 'Save Lead'}
        </button>
      </div>
    </div>
  );
};

export default AddLeadForm;
